import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { CountrySelect } from '@/components/selects';
import { Input } from '@/components/ui/Input';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import type { HrPayrollFormula, HrPayrollRun, HrPayrollTransaction } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { downloadBlob } from '@/utils/downloadBlob';
import { notify } from '@/core/toast/notify';

export function HrPayrollPage() {
  const pagination = usePagination();
  const [runs, setRuns] = useState<HrPayrollRun[]>([]);
  const [formulas, setFormulas] = useState<HrPayrollFormula[]>([]);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('IN');
  const [runCode, setRunCode] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [creating, setCreating] = useState(false);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactions, setTransactions] = useState<HrPayrollTransaction[]>([]);
  const [transactionsRun, setTransactionsRun] = useState<HrPayrollRun | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.listPayrollRuns({ page: pagination.page, size: pagination.size });
      setRuns(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  const loadFormulas = useCallback(async () => {
    try {
      setFormulas(await hrApi.listFormulas(countryCode || undefined));
    } catch {
      setFormulas([]);
    }
  }, [countryCode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadFormulas();
  }, [loadFormulas]);

  const createRun = async () => {
    if (!runCode.trim() || !periodStart || !periodEnd) {
      notify.error('Run code and period dates are required');
      return;
    }
    setCreating(true);
    try {
      await hrApi.createPayrollRun({
        runCode: runCode.trim(),
        periodStart,
        periodEnd,
        currencyCode: 'INR',
      });
      notify.success('Payroll run created');
      setRunCode('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create payroll run');
    } finally {
      setCreating(false);
    }
  };

  const calculate = async (id: number) => {
    try {
      await hrApi.calculatePayroll(id, countryCode || undefined);
      notify.success('Payroll calculated and processed');
      load();
    } catch (err) {
      showApiError(err, 'Payroll calculation failed — approve the run first');
    }
  };

  const approve = async (id: number) => {
    try {
      await hrApi.approvePayrollRun(id);
      notify.success('Payroll run approved');
      load();
    } catch (err) {
      showApiError(err, 'Payroll approval failed');
    }
  };

  const exportStatutory = async (id: number) => {
    try {
      const blob = await hrApi.downloadStatutoryIndiaCsv(id);
      downloadBlob(blob, `statutory-india-${id}.csv`);
      notify.success('Statutory report exported');
    } catch (err) {
      showApiError(err, 'Statutory export failed');
    }
  };

  const openTransactions = async (run: HrPayrollRun) => {
    setTransactionsRun(run);
    setTransactionsOpen(true);
    setTransactionsLoading(true);
    try {
      setTransactions(await hrApi.listPayrollTransactions(run.id));
    } catch (err) {
      showApiError(err, 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const transactionColumns: Column<HrPayrollTransaction>[] = [
    { key: 'employeeId', header: 'Employee ID' },
    { key: 'grossAmount', header: 'Gross' },
    { key: 'deductionAmount', header: 'Deductions' },
    { key: 'netAmount', header: 'Net' },
  ];

  const canCalculate = (row: HrPayrollRun) =>
    row.statusCode !== 'PROCESSED' && row.statusCode === 'APPROVED';

  const columns: Column<HrPayrollRun>[] = [
    { key: 'runCode', header: 'Run' },
    { key: 'periodStart', header: 'From' },
    { key: 'periodEnd', header: 'To' },
    { key: 'statusCode', header: 'Status' },
    {
      key: 'actions',
      header: 'Actions',
      actionsWrap: true,
      render: (row) => (
        <TableRowActions layout="wrap">
          {row.statusCode !== 'PROCESSED' && row.statusCode !== 'APPROVED' && (
            <Button variant="secondary" size="sm" onClick={() => approve(row.id)}>
              Approve
            </Button>
          )}
          {row.statusCode !== 'PROCESSED' && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canCalculate(row)}
              title={
                canCalculate(row)
                  ? formulas.length > 0
                    ? `Calculate using ${formulas.length} formula(s)`
                    : 'Calculate using statutory fallback rate'
                  : 'Approve this run before calculating'
              }
              onClick={() => calculate(row.id)}
            >
              Calculate
            </Button>
          )}
          {row.statusCode === 'PROCESSED' && (
            <>
              <Button variant="secondary" size="sm" onClick={() => exportStatutory(row.id)}>
                IN statutory
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  try {
                    const blob = await hrApi.downloadDisbursementCsv(row.id);
                    downloadBlob(blob, `disbursement-${row.runCode}.csv`);
                  } catch (err) {
                    showApiError(err, 'Disbursement export failed');
                  }
                }}
              >
                Bank file
              </Button>
              <Button variant="secondary" size="sm" onClick={() => void openTransactions(row)}>
                Transactions
              </Button>
            </>
          )}
        </TableRowActions>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Payroll"
        description="Country templates, salary calculation, and finance GL integration"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Payroll' }]}
      />

      <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <h3 className="mb-2 text-sm font-medium">New payroll run</h3>
        <div className="flex flex-wrap items-end gap-2">
          <Input label="Run code" value={runCode} onChange={(e) => setRunCode(e.target.value)} />
          <DateRangeFields
            from={periodStart}
            to={periodEnd}
            onFromChange={setPeriodStart}
            onToChange={setPeriodEnd}
            fromLabel="Period start"
            toLabel="Period end"
          />
          <Button loading={creating} onClick={() => void createRun()}>
            Create run
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
          Workflow: create run → approve → calculate. Calculation uses org formulas when configured,
          otherwise a statutory fallback rate.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <CountrySelect
          label="Country (formulas)"
          value={countryCode}
          onChange={setCountryCode}
          className="max-w-xs"
        />
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {formulas.length > 0
            ? `${formulas.length} active formula(s): ${formulas.map((f) => f.targetComponentCode).join(', ')}`
            : 'No country formulas — fallback statutory rate will apply'}
        </span>
      </div>

      <DataTable columns={columns} data={runs} loading={loading} keyField="id" {...dataTablePageProps(pagination)} />

      <Modal
        open={transactionsOpen}
        onClose={() => setTransactionsOpen(false)}
        title={transactionsRun ? `Transactions — ${transactionsRun.runCode}` : 'Payroll transactions'}
        size="lg"
      >
        <DataTable
          columns={transactionColumns}
          data={transactions}
          loading={transactionsLoading}
          keyField="id"
        />
      </Modal>
    </div>
  );
}
